import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminTopbar from './AdminTopbar';
import ImpersonationBanner from '../shared/ImpersonationBanner';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';

const AdminBottomNav = ({ onMenuToggle, isSidebarOpen, shellConfig = null }) => {
  const { isDark } = useTheme();
  const { hasPermission } = useAdminAuth();
  const { t } = useTranslation('admin');
  const location = useLocation();
  const navigate = useNavigate();
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [isAiMenuAnimating, setIsAiMenuAnimating] = useState(false);

  if (shellConfig?.hideBottomNav) {
    return null;
  }

  const isMenuActive = !!isSidebarOpen;

  const navItems = [
    { path: '/admin', label: t('admin.nav_dashboard'), icon: 'dashboard', permission: 'dashboard' },
    { path: '/admin/bookings', label: t('admin.nav_bookings'), icon: 'calendar', permission: 'bookings' },
  ];

  const rightNavItems = [
    { path: '/admin/clients', label: t('admin.nav_clients'), icon: 'users', permission: 'clients' },
  ];

  const aiToolsItems = [
    { path: '/admin/haircuts-studio', label: t('admin.nav_haircuts_studio'), icon: 'scissors' },
    { path: '/admin/makeup-studio', label: t('admin.nav_makeup_studio'), icon: 'lipstick' },
    // { path: '/admin/botox-filler-studio', label: 'Botox & Filler', icon: 'syringe' }, // HIDDEN: Botox & Filler feature disabled
    { path: '/admin/nails-studio', label: t('admin.nav_nails_studio'), icon: 'hand-nails' },
    { path: '/admin/ai-analysis', label: t('admin.nav_ai_analysis'), icon: 'ai-scan' },
    { path: '/admin/suggestions', label: t('admin.nav_hairstyle_suggestions'), icon: 'lightbulb' },
  ];

  const filteredNavItems = navItems.filter(item => hasPermission(item.permission));
  const filteredRightNavItems = rightNavItems.filter(item => hasPermission(item.permission));
  // In the mobile/tablet bottom nav, we want the menu toggle on the left,
  // and the Dashboard/Home button on the right (where the menu button used to be).
  const leftNavItems = filteredNavItems.filter((item) => item.path !== '/admin');
  const rightNavItemsCombined = [
    ...filteredNavItems.filter((item) => item.path === '/admin'),
    ...filteredRightNavItems,
  ];

  const isAiToolsActive = () => {
    return aiToolsItems.some(item => location.pathname === item.path);
  };

  const handleOpenAiMenu = () => {
    setShowAiMenu(true);
    setIsAiMenuAnimating(true);
  };

  const handleCloseAiMenu = () => {
    setIsAiMenuAnimating(false);
    setTimeout(() => setShowAiMenu(false), 200);
  };

  const IconComponent = ({ iconName, className = "h-6 w-6" }) => {
    const iconProps = {
      className,
      fill: "none",
      stroke: "currentColor",
      viewBox: "0 0 24 24",
      strokeWidth: 2
    };

    switch (iconName) {
      case 'dashboard':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'calendar':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'users':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'menu':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        );
      case 'ai-sparkles':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7l1.5 1.5L7 10M17 7l-1.5 1.5L17 10" />
          </svg>
        );
      case 'scissors':
        return (
          <svg {...iconProps}>
            <circle cx="6" cy="6" r="2" />
            <circle cx="6" cy="18" r="2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7l12 10M8 17l12-10" />
          </svg>
        );
      case 'lipstick':
        return (
          <svg {...iconProps}>
            <rect x="9" y="12" width="6" height="9" rx="1" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 12V8a2 2 0 014 0v4" />
            <ellipse cx="12" cy="8" rx="2.5" ry="1.5" />
            <line x1="9" y1="16" x2="15" y2="16" strokeLinecap="round" />
            <line x1="9" y1="18" x2="15" y2="18" strokeLinecap="round" />
          </svg>
        );
      case 'makeup-brush':
        return (
          <svg {...iconProps}>
            <ellipse cx="8" cy="16" rx="2.5" ry="3" transform="rotate(-25 8 16)" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 14.5l8-8M17.5 6.5l2-2a1.5 1.5 0 012 0 1.5 1.5 0 010 2l-2 2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l7.5-7.5" strokeWidth={1.5} />
          </svg>
        );
      case 'palette':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="8" />
            <circle cx="8.5" cy="9" r="1" />
            <circle cx="11" cy="7" r="1" />
            <circle cx="7" cy="12.5" r="1" />
            <circle cx="10" cy="15" r="1" />
            <circle cx="16" cy="13" r="2" />
          </svg>
        );
      case 'syringe':
        return (
          <svg {...iconProps}>
            <rect x="4" y="10" width="10" height="4" rx="1" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 12h6M4 12H2M2 10v4M6 8v2M9 8v2M12 8v2" />
          </svg>
        );
      case 'hand-nails':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 13V8a1 1 0 012 0v5m0 0V7a1 1 0 112 0v6m0 0V7a1 1 0 112 0v7m0 0V9a1 1 0 112 0v8a4 4 0 01-4 4H9a4 4 0 01-4-4v-1a3 3 0 013-3h1z" />
            <circle cx="9" cy="6.5" r="0.8" fill="currentColor" />
            <circle cx="11" cy="5.5" r="0.8" fill="currentColor" />
            <circle cx="13" cy="5.5" r="0.8" fill="currentColor" />
            <circle cx="15" cy="7.5" r="0.8" fill="currentColor" />
          </svg>
        );
      case 'nail-polish':
        return (
          <svg {...iconProps}>
            <rect x="9" y="14" width="6" height="7" rx="1" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14V9a2 2 0 014 0v5" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 9V7a1 1 0 012 0v2" />
            <line x1="10.5" y1="17" x2="13.5" y2="17" strokeLinecap="round" />
          </svg>
        );
      case 'hand':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V6a1 1 0 012 0v5m0 0V5a1 1 0 112 0v6m0 0V5a1 1 0 112 0v7m0 0V7a1 1 0 112 0v9a4 4 0 01-4 4H8a4 4 0 01-4-4v-1a3 3 0 013-3h1z" />
          </svg>
        );
      case 'sparkles':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'ai-scan':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="3" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v2M12 20v2M2 12h2M20 12h2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            <circle cx="12" cy="12" r="7" strokeDasharray="2 3" opacity="0.5" />
            <circle cx="12" cy="12" r="9.5" strokeDasharray="3 4" opacity="0.3" />
          </svg>
        );
      case 'cpu-chip':
        return (
          <svg {...iconProps}>
            <rect x="7" y="7" width="10" height="10" rx="2" />
            <rect x="10" y="10" width="4" height="4" rx="1" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 1v4M15 1v4M9 19v4M15 19v4M1 9h4M1 15h4M19 9h4M19 15h4" />
          </svg>
        );
      case 'brain':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'lightbulb':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'magic-wand':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* AI Tools Menu Modal - Professional Bottom Sheet */}
      {showAiMenu && (
        <div
          className="fixed inset-0 z-50 flex items-end min-[1025px]:hidden"
          onClick={handleCloseAiMenu}
          style={{
            opacity: isAiMenuAnimating ? 1 : 0,
            transition: 'opacity 180ms ease-out',
            pointerEvents: showAiMenu ? 'auto' : 'none'
          }}
        >
          {/* Backdrop - Soft blur */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.25)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          />
          
          {/* Bottom Sheet */}
          <div
            className={`relative w-full rounded-t-3xl p-6 pb-[calc(env(safe-area-inset-bottom,0px)+24px)] ${
              isDark 
                ? 'bg-gray-900/98 border-t' 
                : 'bg-white border-t'
            }`}
            style={{ 
              transform: isAiMenuAnimating ? 'translateY(0)' : 'translateY(100%)',
              transition: 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
              maxHeight: '65vh',
              overflowY: 'auto',
              borderColor: isDark ? 'rgba(201,160,56,0.2)' : 'rgba(125,102,51,0.1)',
              boxShadow: isDark 
                ? '0 -8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,160,56,0.1)' 
                : '0 -8px 32px rgba(0,0,0,0.12)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 
                  className="text-base font-semibold"
                  style={{ color: isDark ? '#f0d9a6' : '#7d6633' }}
                >
                  {t('admin.nav_ai_tools')}
                </h3>
                <p 
                  className="text-xs mt-0.5"
                  style={{ color: isDark ? '#a8b0b8' : '#6f6f6f' }}
                >
                  {t('admin.ai_tools_subtitle')}
                </p>
              </div>
              <button
                onClick={handleCloseAiMenu}
                className="p-2 rounded-xl transition-all duration-150 active:scale-95"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  color: isDark ? '#e7ebef' : '#5a5a5a'
                }}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 gap-3">
              {aiToolsItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      handleCloseAiMenu();
                    }}
                    className="flex flex-col items-center p-4 rounded-2xl transition-all duration-150 active:scale-98"
                    style={{
                      background: isActive
                        ? isDark
                          ? 'rgba(201,160,56,0.12)'
                          : 'rgba(125,102,51,0.08)'
                        : isDark
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.02)',
                      border: isActive 
                        ? `1.5px solid ${isDark ? 'rgba(201,160,56,0.4)' : 'rgba(125,102,51,0.3)'}`
                        : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                      transform: 'scale(1)',
                    }}
                  >
                    <div 
                      className="mb-2.5 transition-colors duration-150"
                      style={{ 
                        color: isActive 
                          ? (isDark ? '#f0d9a6' : '#7d6633')
                          : (isDark ? '#a8b0b8' : '#6f6f6f')
                      }}
                    >
                      <IconComponent iconName={item.icon} className="h-6 w-6" />
                    </div>
                    <span 
                      className="text-xs font-medium text-center leading-tight transition-colors duration-150"
                      style={{ 
                        color: isActive 
                          ? (isDark ? '#f0d9a6' : '#7d6633')
                          : (isDark ? '#e9edf1' : '#2f2f2f')
                      }}
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Mobile + Tablet (hidden on desktop only) */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 min-[1025px]:hidden"
        role="navigation"
        style={{ 
          pointerEvents: 'none',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)'
        }}
      >
        {/* Wrapper Container - Compact & Professional */}
        <div className="relative px-2 pt-6 pb-2">
          
          {/* Center AI Tools Button - Smaller & Sleeker */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 z-10">
            <button
              onClick={handleOpenAiMenu}
              className="transition-all duration-150 active:scale-96"
              style={{
                background: `linear-gradient(135deg, ${isDark ? '#c9a038' : '#d4a84a'} 0%, ${isDark ? '#8d7239' : '#7d6633'} 100%)`,
                boxShadow: isDark
                  ? `0 4px 16px rgba(201,160,56,0.3), 
                     0 0 0 3px rgba(26,26,26,1), 
                     0 0 0 4px rgba(201,160,56,0.2),
                     inset 0 1px 0 rgba(255,255,255,0.15)`
                  : `0 4px 16px rgba(125,102,51,0.2), 
                     0 0 0 3px rgba(255,255,255,1), 
                     0 0 0 4px rgba(125,102,51,0.15),
                     inset 0 1px 0 rgba(255,255,255,0.4)`,
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                pointerEvents: 'auto',
                animation: isAiToolsActive() ? 'gentlePulse 3s ease-in-out infinite' : 'none'
              }}
            >
              <IconComponent iconName="ai-sparkles" className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation Bar - Compact & Elegant */}
          <div
            className="rounded-[24px] backdrop-blur-xl"
            style={{ 
              pointerEvents: 'auto',
              background: isDark ? 'rgba(32, 37, 43, 0.75)' : 'rgba(255, 255, 255, 0.88)',
              border: isDark 
                ? '1px solid rgba(201,160,56,0.15)' 
                : '1px solid rgba(125,102,51,0.12)',
              boxShadow: isDark 
                ? '0 -2px 12px rgba(0,0,0,0.35), 0 2px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(201,160,56,0.08)' 
                : '0 -2px 10px rgba(0,0,0,0.05), 0 2px 16px rgba(0,0,0,0.07)'
            }}
          >
            <div className="flex items-center justify-between px-3 py-2">
            {/* Left Nav Items - More Compact */}
            <div className="flex gap-2">
              {/* Menu Button - Compact (left side; sidebar opens from left) */}
              <motion.button
                onClick={onMenuToggle}
                className="flex flex-col items-center min-w-[56px] transition-all duration-150 active:scale-97"
                aria-label={t('admin.toggle_menu')}
                animate={{
                  rotate: isSidebarOpen ? 90 : 0,
                  scale: isSidebarOpen ? 1.2 : 1,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                }}
              >
                <div
                  className="p-2 rounded-xl transition-all duration-150"
                  style={{
                    background: isMenuActive
                      ? isDark
                        ? 'rgba(201,160,56,0.12)'
                        : 'rgba(125,102,51,0.08)'
                      : 'transparent',
                    color: isMenuActive
                      ? isDark
                        ? '#f0d9a6'
                        : '#7d6633'
                      : isDark
                      ? '#a8b0b8'
                      : '#6f6f6f'
                  }}
                >
                  <IconComponent iconName="scissors" className="h-6 w-6" />
                </div>
                <div className="mt-0.5 w-1 h-1" />
              </motion.button>

              {leftNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex flex-col items-center min-w-[56px] transition-all duration-150 active:scale-97"
                  >
                    <div
                      className="p-2 rounded-xl transition-all duration-150"
                      style={{
                        background: isActive
                          ? isDark
                            ? 'rgba(201,160,56,0.12)'
                            : 'rgba(125,102,51,0.08)'
                          : 'transparent',
                        color: isActive
                          ? isDark
                            ? '#f0d9a6'
                            : '#7d6633'
                          : isDark
                          ? '#a8b0b8'
                          : '#6f6f6f'
                      }}
                    >
                      <IconComponent iconName={item.icon} className="h-5 w-5" />
                    </div>
                    {/* Active indicator dot - More subtle */}
                    {isActive && (
                      <div 
                        className="mt-0.5 w-1 h-1 rounded-full"
                        style={{ 
                          background: isDark ? '#c9a038' : '#7d6633',
                          boxShadow: isDark 
                            ? '0 0 8px rgba(201,160,56,0.6)' 
                            : '0 0 6px rgba(125,102,51,0.4)'
                        }}
                      />
                    )}
                    {!isActive && <div className="mt-1 w-1 h-1" />}
                  </Link>
                );
              })}
            </div>

            {/* Right Nav Items - More Compact */}
            <div className="flex gap-2">
              {rightNavItemsCombined.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex flex-col items-center min-w-[56px] transition-all duration-150 active:scale-97"
                  >
                    <div
                      className="p-2 rounded-xl transition-all duration-150"
                      style={{
                        background: isActive
                          ? isDark
                            ? 'rgba(201,160,56,0.12)'
                            : 'rgba(125,102,51,0.08)'
                          : 'transparent',
                        color: isActive
                          ? isDark
                            ? '#f0d9a6'
                            : '#7d6633'
                          : isDark
                          ? '#a8b0b8'
                          : '#6f6f6f'
                      }}
                    >
                      <IconComponent iconName={item.icon} className="h-5 w-5" />
                    </div>
                    {isActive && (
                      <div 
                        className="mt-0.5 w-1 h-1 rounded-full"
                        style={{ 
                          background: isDark ? '#c9a038' : '#7d6633',
                          boxShadow: isDark 
                            ? '0 0 8px rgba(201,160,56,0.6)' 
                            : '0 0 6px rgba(125,102,51,0.4)'
                        }}
                      />
                    )}
                    {!isActive && <div className="mt-0.5 w-1 h-1" />}
                  </Link>
                );
              })}

            </div>
          </div>
        </div>
        </div>
      </nav>

      <style>{`
        /* Micro-animations - Professional & Subtle */
        .active\\:scale-97:active {
          transform: scale(0.97);
        }
        .active\\:scale-96:active {
          transform: scale(0.96);
        }
        .active\\:scale-98:active {
          transform: scale(0.98);
        }
        .active\\:scale-95:active {
          transform: scale(0.95);
        }

        /* Gentle pulse for active AI button - very subtle */
        @keyframes gentlePulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.92;
            transform: scale(0.98);
          }
        }

        /* Ensure smooth scrolling on iOS */
        nav {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        /* Global mobile + tablet bottom spacing for fixed nav */
        @media (max-width: 1024px) {
          :root {
            /* Reserve space so content never sits under the fixed bottom nav */
            --hc-admin-bottom-nav-offset: 132px;
          }

          /* Keep a little breathing room on the content container itself */
          .hc-mobile-bottom-pad {
            padding-bottom: 16px;
          }

          /* True spacer at the end of scroll content (the important part) */
          .hc-admin-bottom-spacer {
            height: calc(env(safe-area-inset-bottom, 0px) + var(--hc-admin-bottom-nav-offset));
          }

          /* Helps scrollIntoView / anchor focus not land under the nav */
          main {
            scroll-padding-bottom: calc(env(safe-area-inset-bottom, 0px) + var(--hc-admin-bottom-nav-offset));
          }
        }
      `}</style>
    </>
  );
};

const AdminLayout = ({ children, shellConfig = null }) => {
  const { isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [hasImpersonationBanner, setHasImpersonationBanner] = useState(false);

  // Check if impersonation banner is present
  React.useEffect(() => {
    const checkBanner = () => {
      const banner = document.querySelector('[data-impersonation-banner]');
      setHasImpersonationBanner(!!banner);
    };
    
    checkBanner();
    const interval = setInterval(checkBanner, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-screen transition-all duration-500 ease-out relative overflow-hidden">
      {/* Luxury Gradient Background - Dark Theme */}
      {isDark ? (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>
          
          {/* Abstract Shapes - Dark */}
          <div className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
          
          {/* Floating Abstract Lines - Dark */}
          <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }}></div>
          <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }}></div>
        </>
      ) : (
        <>
          {/* Light Theme Background - Pure White */}
          <div className="absolute inset-0 bg-white pointer-events-none"></div>
        </>
      )}
      
      {/* Content Container - Flex column layout */}
      <div className={`relative z-10 h-full flex flex-col overflow-hidden ${isDark ? 'text-white theme-dark' : 'text-gray-900 theme-light'}`}>
        {/* Impersonation Banner - appears at the very top */}
        <ImpersonationBanner />
        
        {/* Header - Fixed height, never scrolls */}
        <div className={`${hasImpersonationBanner ? 'h-32' : 'h-24'} flex-shrink-0`}>
          <AdminTopbar 
            onMenuToggle={() => setSidebarOpen(!sidebarOpen)} 
            onSearchStateChange={setIsSearchActive}
            hasImpersonationBanner={hasImpersonationBanner}
            shellConfig={shellConfig}
          />
        </div>
        
        <AdminSidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          isSearchActive={isSearchActive}
          hasImpersonationBanner={hasImpersonationBanner}
          shellConfig={shellConfig}
        />
        
        {/* Main content - ONLY scrollable area */}
        <main
          className={`flex-1 overflow-y-auto ${
            sidebarCollapsed ? 'min-[1025px]:ml-16' : 'min-[1025px]:ml-64'
          } transition-all duration-300 ease-in-out`}
        >
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8 md:pb-4 w-full hc-mobile-bottom-pad">
            <div className="w-full">
              {children}
            </div>
            {/* Ensures last section is never covered by fixed bottom nav (mobile + tablet) */}
            <div className="hc-admin-bottom-spacer" aria-hidden="true" />
          </div>
        </main>
        
        <AdminBottomNav
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          isSidebarOpen={sidebarOpen}
          shellConfig={shellConfig}
        />
      </div>
    </div>
  );
};

export default AdminLayout;
